import { useState } from 'react';
import { Container, Title, Button, Group, Stack, Text, Alert, Paper, Table, ActionIcon, Badge, Menu } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPlus, IconInfoCircle, IconEdit, IconTrash, IconDotsVertical, IconShieldCheck } from '@tabler/icons-react';
import { useOrganization } from '@/hooks/useOrganization';
import { useCustomRoles, useDeleteCustomRole } from '@/hooks/api/useCustomRoles';
import { CustomRoleModal } from './components/CustomRoleModal';
import type { CustomRole } from '@/hooks/api/useCustomRoles';

export function RolesPage() {
  const { organizationId, isAdmin } = useOrganization();
  const { data: roles = [], isLoading } = useCustomRoles(organizationId || '');
  const deleteRole = useDeleteCustomRole();

  const [modalOpened, setModalOpened] = useState(false);
  const [selectedRole, setSelectedRole] = useState<CustomRole | null>(null);

  const handleCreate = () => {
    setSelectedRole(null);
    setModalOpened(true);
  };

  const handleEdit = (role: CustomRole) => {
    setSelectedRole(role);
    setModalOpened(true);
  };

  const handleDelete = (role: CustomRole) => {
    modals.openConfirmModal({
      title: 'Delete Custom Role',
      children: (
        <Text size="sm">
          Are you sure you want to delete the <strong>{role.name}</strong> role?
          Users with this role will keep their current permissions but the role template will be removed.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteRole.mutate(role.id);
      },
    });
  };

  // Check if user is admin
  if (!isAdmin) {
    return (
      <Container fluid>
        <Alert icon={<IconInfoCircle size={16} />} color="red">
          <Text size="sm">
            You do not have permission to access this page. Only administrators can manage roles.
          </Text>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Stack gap="lg">
        <Group justify="apart">
          <div>
            <Title order={1}>Custom Roles</Title>
            <Text c="dimmed" size="sm">
              Create reusable role templates with specific permissions
            </Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} onClick={handleCreate}>
            Create Role
          </Button>
        </Group>

        <Alert icon={<IconInfoCircle size={16} />} color="blue">
          <Text size="sm">
            Custom roles are permission templates that you can assign to multiple users.
            When you assign a role to a user, their permissions are automatically set to match the role.
          </Text>
        </Alert>

        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Role Name</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th>Pages Enabled</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading ? (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text ta="center" py="xl" c="dimmed">
                      Loading roles...
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : roles.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Stack align="center" gap="md" py="xl">
                      <IconShieldCheck size={48} color="gray" />
                      <div>
                        <Text ta="center" fw={500}>
                          No custom roles yet
                        </Text>
                        <Text ta="center" c="dimmed" size="sm">
                          Create a custom role to define reusable permission templates
                        </Text>
                      </div>
                      <Button leftSection={<IconPlus size={16} />} onClick={handleCreate}>
                        Create Your First Role
                      </Button>
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              ) : (
                roles.map((role) => {
                  const enabledPages = Object.entries(role.permissions || {})
                    .filter(([_, perms]: [string, any]) => perms.enabled)
                    .map(([page]) => page);

                  return (
                    <Table.Tr key={role.id}>
                      <Table.Td>
                        <Text fw={500}>{role.name}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {role.description || 'No description'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          {enabledPages.length > 0 ? (
                            <>
                              {enabledPages.slice(0, 3).map((page) => (
                                <Badge key={page} size="sm" variant="light">
                                  {page}
                                </Badge>
                              ))}
                              {enabledPages.length > 3 && (
                                <Badge size="sm" variant="light" color="gray">
                                  +{enabledPages.length - 3} more
                                </Badge>
                              )}
                            </>
                          ) : (
                            <Text size="sm" c="dimmed">
                              No pages enabled
                            </Text>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Menu shadow="md" width={200} position="bottom-end">
                          <Menu.Target>
                            <ActionIcon variant="subtle" color="gray">
                              <IconDotsVertical size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              leftSection={<IconEdit size={14} />}
                              onClick={() => handleEdit(role)}
                            >
                              Edit Role
                            </Menu.Item>
                            <Menu.Item
                              color="red"
                              leftSection={<IconTrash size={14} />}
                              onClick={() => handleDelete(role)}
                            >
                              Delete Role
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Table.Td>
                    </Table.Tr>
                  );
                })
              )}
            </Table.Tbody>
          </Table>
        </Paper>

        {roles.length > 0 && (
          <Text size="sm" c="dimmed">
            Showing {roles.length} custom role{roles.length !== 1 ? 's' : ''}
          </Text>
        )}

        <CustomRoleModal
          opened={modalOpened}
          onClose={() => {
            setModalOpened(false);
            setSelectedRole(null);
          }}
          role={selectedRole}
        />
      </Stack>
    </Container>
  );
}
